import ProjectPage from "../ProjectPage";
import IssuePage from "../IssuePage";

import "./style.scss"
import { createContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { getJiraTrackedTime } from "../../services/jira.service";
import { getOpenProjectTrackedTime } from "../../services/openProject.service";
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import { clearSession } from "../../App";
import { setUserJiraSession, setUserOpSession } from "../../store/userSlice";
import FullScreenPopup from "../../components/popup/fullScreenPopup";
import { Alert, Button, Snackbar } from "@mui/material";
import { setTrackingState } from "../../store/trackingSlice";
import { io } from 'socket.io-client';
import { setCurrentDocumentationSteps, setDocumentationContent, setOpenCreateDocumentation, setSelectedDocumentSteps } from "../../store/documentSlice";
import { ContainedButton } from "../CreateDocumentationPage/styled";

export const ProjectContext = createContext<any>({});
const cleanupDocsChannel = new BroadcastChannel('cleanup_docs_channel');

const Homepage = () => {
  const [snackbarStatus, setSnackbarStatus] = useState(false)

  const jiraAccessToken = localStorage.getItem('jiraAccessToken');
  const openProjectAccessToken = localStorage.getItem('openProjectAccessToken');
  const jiraSessionIdStorage = localStorage.getItem('jiraSessionId');
  const opSessionIdStorage = localStorage.getItem('opSessionId');

  const userState = useSelector((state: RootState) => state.user);
  const userDetails = userState?.user;

  const projectState = useSelector((state: RootState) => state.project);
  const projectType = projectState.projectType;
  const projectTypeMenu = projectState.projectTypeMenu;
  const newProjectCreated = projectState.newProjectCreated;

  const [projectList, setProjectList] = useState<any[]>([]);
  const [jiraProjectList, setJiraProjectList] = useState<any[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [isAlreadyLoggedIn, setIsAlreadyLoggedIn] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<any | null>(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const opSessionId = useSelector((state: RootState) => state.user.opSessionId);
  const jiraSessionId = useSelector((state: RootState) => state.user.jiraSessionId);

  const contextValue = {
    projectList,
    jiraProjectList,
    loader,
    selectedNotification,
    setSelectedNotification
  }

  const getJiraProjectByUser = async (accountId: string) => {
    try {
      setProjectList([]);
      setLoader(true);
      const jiraProjectResponse = await getJiraTrackedTime(accountId);
      setJiraProjectList([...jiraProjectResponse?.data]);
    } catch (error) {
      toast.error('Failed to fetch Jira project details');
      setJiraProjectList([]);
    } finally {
      setLoader(false);
    }
  }

  const getOpenProjectByUser = async (userId: string, admin: boolean) => {
    try {
      setJiraProjectList([]);
      setLoader(true);
      const openProjectResponse = await getOpenProjectTrackedTime(userId, admin);
      setProjectList([...openProjectResponse?.data]);
    } catch (error) {
      toast.error('Failed to fetch Open project details');
      setProjectList([]);
    } finally {
      setLoader(false);
    }
  }

  useEffect(() => {
    const jiraAccessToken = localStorage.getItem('jiraAccessToken');
    const openProjectAccessToken = localStorage.getItem('openProjectAccessToken');
    if (projectType === projectTypeMenu[2]) {
      if ((jiraAccessToken && userDetails && userDetails.jiraDetails && userDetails.jiraDetails?.accountId)) {
        getJiraProjectByUser(userDetails.jiraDetails.accountId);
      } else {
        setLoader(false);
      }
    } else if ((projectType === projectTypeMenu[0])) {
      if ((openProjectAccessToken && userDetails && userDetails.openProjectDetails && userDetails.openProjectDetails?.id)) {
        getOpenProjectByUser(userDetails.openProjectDetails?.id, userDetails.openProjectDetails?.admin);
      } else {
        setLoader(false);
      }
    } else {
      setLoader(false);
    }
  }, [userDetails, projectType, projectTypeMenu, newProjectCreated])

  const clearStateSession = () => {
    if (opSessionId)
      dispatch(setUserOpSession(null));
    if (jiraSessionId)
      dispatch(setUserJiraSession(null));
  };

  const openDocument = () => {
    dispatch(setSelectedDocumentSteps(['editDoc', "articlePage"]))
    dispatch(setCurrentDocumentationSteps(0))
    dispatch(setOpenCreateDocumentation(true))
    setSnackbarStatus(false)
  }

  useEffect(() => {
    if (
      ((jiraSessionIdStorage !== null && jiraSessionId && jiraSessionId !== jiraSessionIdStorage) ||
        (opSessionIdStorage !== null && opSessionId && opSessionId !== opSessionIdStorage))
    ) {
      setIsAlreadyLoggedIn(true);
    }

    if ((!jiraAccessToken && !openProjectAccessToken)) {
      clearStateSession();
      clearSession();
      dispatch(setTrackingState(false))
      navigate('/login');
    }
  }, []);

  useEffect(() => {
    if (userDetails?.userDetails?.id) {
      // Initialize socket with userId
      const socket = io('/', {
        path: '/ws',
        query: { userId: userDetails?.userDetails?.id },
      });

      // Listen for events
      socket.on('receivedDocData', (data: any) => {
        console.log("data", data);
        if (data.userId) {
          delete data.userId
        }
        dispatch(setDocumentationContent(data))
        setSnackbarStatus(true)
        cleanupDocsChannel.postMessage('cleanup');
      });

      // Cleanup on unmount
      return () => {
        socket.disconnect();
      };
    }
  }, [userDetails?.userDetails?.id]);

  if (isAlreadyLoggedIn) {
    const opSessionId = localStorage.getItem('opSessionId');
    const jiraSessionId = localStorage.getItem('jiraSessionId');
    return <FullScreenPopup isOpen={isAlreadyLoggedIn}>
      You already have an active session in <strong>{opSessionId ? "OpenProject" : jiraSessionId ? "Jira" : "OpenProject"}</strong>
      Would you like to log out from the previous session?
      <Button variant="contained" color="info" sx={{ textTransform: 'none' }}
        onClick={async () => {

          try {
            setIsAlreadyLoggedIn(false);
            await clearSession();
            await dispatch(setTrackingState(false));
            navigate('/login', { replace: true });
          } catch (error) {
            console.error("Error during logout:", error);
          }
        }}>
        Logout
      </Button>
    </FullScreenPopup>
  }

  return (
    <div className="outerDiv">
      <ProjectContext.Provider value={contextValue}>
        <ProjectPage />
        <IssuePage />
        <Snackbar
          open={snackbarStatus}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbarStatus(false)}
            severity={"success"}
          >
            Your document is ready <ContainedButton onClick={openDocument}>Here</ContainedButton>
          </Alert>
        </Snackbar>
      </ProjectContext.Provider>
    </div>
  )
}

export default Homepage